/**
 * Font Awesome Pro 7.2: curated per-icon imports.
 *
 * Strategy: import only the icons we actually use across the site:
 *   - Every icon hardcoded in PHP templates.
 *   - A generous set of tool / UI icons editors can pick from in the
 *     `product_category > Icon` ACF field.
 *   - Brand icons used in the social-link config.
 *
 * Trade-off: bundle stays small (~30-60 KB gzipped instead of ~2.5 MB
 * for whole-style imports), at the cost of needing to add an icon here
 * before an editor can use it. Adding a new icon = one import line +
 * one library.add() entry + rebuild.
 *
 * After populating the library, dom.watch() replaces all
 * `<i class="fa-...">` tags with inline SVG and observes future DOM
 * mutations so dynamically-injected icons get processed too.
 */

import { library, dom, config } from '@fortawesome/fontawesome-svg-core';

// REQUIRED: ships the .svg-inline--fa sizing rules + animation classes.
// Without this, FA renders SVGs but they collapse to 0×0 and are invisible.
import '@fortawesome/fontawesome-svg-core/styles.css';

// --- Sharp Solid (default style: sharper, on-brand for tools) ---
import {
    faArrowRight,
    faArrowUpRight,
    faArrowUpRightFromSquare,
    faAxe,
    faBolt,
    faCheck,
    faEnvelope,
    faFaucetDrip,
    faGear,
    faGears,
    faGlobe,
    faHammer,
    faHammerCrash,
    faHelmetSafety,
    faLink,
    faLocationDot,
    faPhone,
    faPickaxe,
    faPipeValve,
    faPlus,
    faRuler,
    faRulerCombined,
    faRulerHorizontal,
    faRulerTriangle,
    faRulerVertical,
    faScissors,
    faScrewdriver,
    faScrewdriverWrench,
    faToolbox,
    faTrowel,
    faTrowelBricks,
    faUser,
    faUserHelmetSafety,
    faWrench,
    faWrenchSimple,
} from '@awesome.me/kit-28b743f97f/icons/sharp/solid';

// --- Sharp Light (editor-selectable icons via ACF, see irontools_normalize_fa_class) ---
import {
    faAxe                 as faAxeLight,
    faGear                as faGearLight,
    faHammer              as faHammerLight,
    faHelmetSafety        as faHelmetSafetyLight,
    faPickaxe             as faPickaxeLight,
    faRulerCombined       as faRulerCombinedLight,
    faScrewdriver         as faScrewdriverLight,
    faScrewdriverWrench   as faScrewdriverWrenchLight,
    faToolbox             as faToolboxLight,
    faTrowel              as faTrowelLight,
    faWrench              as faWrenchLight,
    faWrenchSimple        as faWrenchSimpleLight,
} from '@awesome.me/kit-28b743f97f/icons/sharp/light';

// --- Duotone Solid (community avatars + accent highlights) ---
import {
    faHammer              as faHammerDuo,
    faScrewdriver         as faScrewdriverDuo,
    faWrench              as faWrenchDuo,
} from '@awesome.me/kit-28b743f97f/icons/duotone/solid';

// --- Brands (footer + contact socials) ---
import {
    faDiscord,
    faFacebookF,
    faInstagram,
    faLinkedin,
    faLinkedinIn,
    faSquareFacebook,
    faSquareInstagram,
    faSquareXTwitter,
    faThreads,
    faTiktok,
    faXTwitter,
    faYoutube,
} from '@awesome.me/kit-28b743f97f/icons/classic/brands';

/**
 * Initialize the Font Awesome library + DOM watcher.
 * Idempotent: safe to call once on app bootstrap.
 */
export default function initFontAwesome() {
    config.autoReplaceSvg = 'nest';
    config.observeMutations = true;

    library.add(
        // Sharp Solid
        faArrowRight,
        faArrowUpRight,
        faArrowUpRightFromSquare,
        faAxe,
        faBolt,
        faCheck,
        faEnvelope,
        faFaucetDrip,
        faGear,
        faGears,
        faGlobe,
        faHammer,
        faHammerCrash,
        faHelmetSafety,
        faLink,
        faLocationDot,
        faPhone,
        faPickaxe,
        faPipeValve,
        faPlus,
        faRuler,
        faRulerCombined,
        faRulerHorizontal,
        faRulerTriangle,
        faRulerVertical,
        faScissors,
        faScrewdriver,
        faScrewdriverWrench,
        faToolbox,
        faTrowel,
        faTrowelBricks,
        faUser,
        faUserHelmetSafety,
        faWrench,
        faWrenchSimple,
        // Sharp Light
        faAxeLight,
        faGearLight,
        faHammerLight,
        faHelmetSafetyLight,
        faPickaxeLight,
        faRulerCombinedLight,
        faScrewdriverLight,
        faScrewdriverWrenchLight,
        faToolboxLight,
        faTrowelLight,
        faWrenchLight,
        faWrenchSimpleLight,
        // Duotone Solid
        faHammerDuo,
        faScrewdriverDuo,
        faWrenchDuo,
        // Brands
        faDiscord,
        faFacebookF,
        faInstagram,
        faLinkedin,
        faLinkedinIn,
        faSquareFacebook,
        faSquareInstagram,
        faSquareXTwitter,
        faThreads,
        faTiktok,
        faXTwitter,
        faYoutube,
    );

    dom.watch();
}
